import React, { useEffect, useMemo, useState } from 'react';

import HeaderGame from './components/header-game';
import { Button, Justify, Layout, Segment } from 'tea-component';
import videoCover from '@op/assets/svg/video-cover.svg';
import { v4 as uuidv4 } from 'uuid';

import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { connect, useHistory } from 'umi';
import FilterBoxCommon from '@/pages/opinion/components/FilterBoxCommon';
import type { SortBy } from 'tea-component/lib/table/addons';
import {
  gameStoreSelect,
  sortByOptions,
} from '@/pages/opinion/components/FilterBoxCommon/constant';
import StatsTable from './components/StatsDetailTables';
import StatsDetailKeyword from './components/keyword';
import CardCommon from '@/pages/opinion/components/CardCommon';
import Charts from './components/charts';
import { getNewStatsDetailFilter, handleViewMore } from '../utils';
import useLanguage from '@/pages/opinion/hooks/useLanguage';

import { useInitBuriedPointInfo } from '@/pages/opinion/operationLog/buriedPoint';
import {
  StatsChannelDetailPoint,
  StatsRegionDetailPoint,
  StatsLanguageDetailPoint,
  StatsVersionDetailPoint,
  StatsTopicDetailPoint,
  StatsKeywordDetailPoint,
} from '@op/operationLog/pages';

import {
  getStatsDetailParams,
  setStatsDetailParams,
  statsDetailIdKey,
} from '@/pages/opinion/utils/storageUtils';
import { getUrlParams } from '@/pages/opinion/components/FilterCommon/utils';
import { defaultFilterValue } from '@/pages/opinion/components/FilterCommon/constants';
import { useDrilldownParams } from '@op/hooks/useDrilldownParams';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import ContentBody from '@/pages/opinion/components/ContentBody';
import CacheExpiredTips from '@/pages/opinion/components/CacheExpiredTips';
import { NO_DATE } from '@/pages/opinion/constants';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import LanguageSubtitle from './components/languageSubtitle';
import { JumpType, navigate } from '@home/utils/utils';
const { Content } = Layout;
export type ChannelType = 'comments' | 'social';

export type StatsDetailType =
  | 'competitor-keyword'
  | 'competitor-topic'
  | 'performance-keyword'
  | 'steam-performance-keyword-insights'
  | 'steam-performance-keyword-ratings'
  | 'performance-keyword-timeline' // 通用的 keyword 折线图
  | 'performance-channel' // performance 页面的渠道图表
  | 'performance-language' // performance 页面的语言图表
  | 'performance-region' // performance 页面的区域图表
  | 'performance-market' // performance 页面的国家图表
  | 'steam-ratings-language' // steam 页面的语言图标
  | 'steam-insights-language' // steam 页面的语言图标
  | 'stats-mentions'
  | 'stats-timeline-of-mentions'
  | 'stats-timeline-of-sentiment'
  | 'stats-sentiment'
  | 'influencer-keyword'
  | 'accounts-channel-keyword'
  | 'accounts-account-keyword'
  | 'hero-keyword'
  | 'summary-keyword'
  | 'topic-tracking-keyword'
  | 'Nikke-keyword'
  | 'RealTimeKeyMetrics-keyword'
  | 'KeyOpinions-keyword'
  | 'OpinionLargeScreen-hotkeyword'
  | 'OpinionLargeScreen-rising';

const titleMap: any = {
  'competitor-keyword': 'Competitor',
  'competitor-topic': 'Competitor',
  'performance-keyword': 'Performance',
  'performance-keyword-timeline': 'Performance',
  'performance-channel': 'Performance',
  'steam-ratings-language': 'Steam Ratings',
  'steam-insights-language': 'Insights',
  'performance-language': 'Performance',
  'performance-region': 'Performance',
  'performance-market': 'Performance',
  'steam-performance-keyword-insights': 'Insights',
  'steam-performance-keyword-ratings': 'Steam Ratings',
  'stats-mentions': 'Mentions',
  'stats-timeline-of-mentions': 'Timeline of Mentions',
  'stats-timeline-of-sentiment': 'Timeline of Sentiment',
  'stats-sentiment': 'Sentiment',
  'summary-keyword': 'Summary',
  'influencer-keyword': 'Brand Creator List',
  'accounts-account-keyword': 'By Account',
  'accounts-channel-keyword': 'By Channel',
  'hero-keyword': 'Hero',
  'topic-tracking-keyword': 'Topic Tracking',
  'Nikke-keyword': 'Nikke',
  'RealTimeKeyMetrics-keyword': 'Key Metrics (Real-time)',
  'KeyOpinions-keyword': 'Key Opinions AI Analysis',
  'OpinionLargeScreen-hotkeyword': 'Keyword Related Reviews',
  'OpinionLargeScreen-rising': 'Keyword Related Reviews',
  'VersionAnalysis-keyword': 'Version Analysis',
};

// 记录需要默认选中 Keyword 的列表
export const keywordStatsDetailType: StatsDetailType[] = [
  'influencer-keyword',
  'competitor-keyword',
  'competitor-topic',
  'performance-keyword',
  'RealTimeKeyMetrics-keyword',
  'KeyOpinions-keyword',
  'steam-performance-keyword-insights',
  'steam-performance-keyword-ratings',
  'accounts-channel-keyword',
  'accounts-account-keyword',
  'summary-keyword',
  'hero-keyword',
  'topic-tracking-keyword',
  'Nikke-keyword',
  'OpinionLargeScreen-hotkeyword',
  'OpinionLargeScreen-rising',
];

// 记录上报是 keywordDetail 的列表

export const keywordDetailPoint: StatsDetailType[] = [
  'competitor-keyword',
  'performance-keyword',
  'steam-performance-keyword-insights',
  'steam-performance-keyword-ratings',
  'performance-keyword-timeline',
  'summary-keyword',
  'influencer-keyword',
  'accounts-channel-keyword',
  'accounts-account-keyword',
  'hero-keyword',
  'topic-tracking-keyword',
  'Nikke-keyword',
];

function StatsDetail({ userInfo, gameInfo, wholeGameInfo }: any) {
  const { formatMessage } = useLanguage();
  const history = useHistory();
  const { readParams, loading } = useDrilldownParams();
  const { downloadLegacy } = useDownloadParams();
  const [needBrandHealth, setNeedBrandHealth] = useState(false);
  const [filter, setFilter] = useState<any>(null);
  /** 参数读取失败（缓存失效）时展示兜底提示 */
  const [invalid, setInvalid] = useState(false);
  const [hasHeaderGame, setHasHeaderGame] = useState(false); // 是否有头部游戏信息
  const [headerGameInfo, setHeaderGameInfo] = useState<any>(null);
  const [title, setTitle] = useState(''); // 标题信息
  const [defaultSelected, setDefaultSelect] = useState('');
  const [statsDetailType, setStatsDetailType] = useState<StatsDetailType>(
    'performance-keyword',
  );
  const [statsDetailChartsData, setStatsDetailChartsData] = useState<any>({});
  const [feedsCommentsLoading, setFeedsCommentsLoading] = useState(false);
  const [buriedPoint, setBuriedPoint] = useState<any>(null);
  const [rankBy, setRankBy] = useState(null);

  /**
   * 应用 statsDetail 参数到页面状态
   */
  const applyStatsDetailParams = (curDownLoadParams: any) => {
    setHasHeaderGame(curDownLoadParams.hasHeaderGame || false);
    setHeaderGameInfo(curDownLoadParams.headerGameInfo || null); 
    setStatsDetailType(curDownLoadParams.statsDetailType || '');
    setFilter(curDownLoadParams.filter || null);
    setTitle(titleMap[curDownLoadParams.statsDetailType || ''] || ' ');
    setDefaultSelect(curDownLoadParams.selected || '');
    setNeedBrandHealth(curDownLoadParams.needBrandHealth || false);
    setStatsDetailChartsData(curDownLoadParams.statsDetailChartsData || {});
    setRankBy(curDownLoadParams.rankBy || null);

    if (
      curDownLoadParams.statsDetailType?.startsWith('stats') &&
      curDownLoadParams.statsDetailChartsData?.dataType
    ) {
      const curDataType = curDownLoadParams.statsDetailChartsData?.dataType;
      let curPoint = null;
      switch (curDataType) {
        case 'channel':
          curPoint = StatsChannelDetailPoint;
          break;
        case 'region':
          curPoint = StatsRegionDetailPoint;
          break;
        case 'language':
          curPoint = StatsLanguageDetailPoint;
          break;
        case 'version':
          curPoint = StatsVersionDetailPoint;
          break;
        case 'topic':
          curPoint = StatsTopicDetailPoint;
          break;
        case 'keyword':
          curPoint = StatsKeywordDetailPoint;
          break;
        default:
          break;
      }
      curPoint && setBuriedPoint(curPoint);
    } else if (keywordDetailPoint.includes(curDownLoadParams.statsDetailType)) {
      setBuriedPoint(StatsKeywordDetailPoint);
    } else if (curDownLoadParams.statsDetailType === 'competitor-topic') {
      setBuriedPoint(StatsTopicDetailPoint);
    } else if (curDownLoadParams.statsDetailType === 'performance-channel') {
      setBuriedPoint(StatsChannelDetailPoint);
    } else if (
      [
        'performance-language',
        'steam-ratings-language',
        'steam-insights-language',
      ].includes(curDownLoadParams.statsDetailType)
    ) {
      setBuriedPoint(StatsLanguageDetailPoint);
    } else if (
      curDownLoadParams.statsDetailType === 'performance-region' ||
      curDownLoadParams.statsDetailType === 'performance-market'
    ) {
      setBuriedPoint(StatsRegionDetailPoint);
    }
  };

  useEffect(() => {
    async function loadParams() {
      try {
        // 方案 1：尝试从服务端/本地存储读取（由 Hook 内部处理）
        let serverParams = await readParams();
        // goPage 存储的数据结构为 { filter, path, extra, stats }，完整参数（filter/statsDetailType）存放在 filter 字段中，需解包
        if (
          serverParams &&
          !serverParams.statsDetailType &&
          serverParams?.filter?.statsDetailType
        ) {
          serverParams = serverParams.filter;
        }
        if (serverParams) {
          applyStatsDetailParams(serverParams);
          return;
        }

        // 方案 2：原有 sessionStorage 逻辑（兼容无 _s 标记的旧 URL）
        const curDownLoadParams = getStatsDetailParams();
        const urlInfo = getUrlParams(history);

        if (urlInfo) {
          const { query } = history.location || {};
          const { clickName, titleName, keywordChild } = query;

          if (titleName === 'Overview' && clickName) {
            const defaultInfo = defaultFilterValue();
            const curFilter = {
              ...defaultInfo,
              ...urlInfo,
              keyword: keywordChild?.split(','),
            };
            delete curFilter['channel'];
            setFilter(curFilter);
            setTitle('Performance');
            setStatsDetailType('performance-keyword');
            setBuriedPoint(StatsKeywordDetailPoint);
          }
        } else if (curDownLoadParams) {
          applyStatsDetailParams(curDownLoadParams);
        } else {
          setInvalid(true);
        }
      } catch {}
    }
    loadParams();
  }, []);

  useInitBuriedPointInfo({ buriedPoint });

  /**
   * comments => game store
   * social => social media
   */

  const [channelType, setChannelType] = useState<ChannelType>('social');
  const [total, setTotal] = useState(0);
  const [totalMap, setTotalMap] = useState<any>({});

  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'engagement',
    order: 'desc',
  });

  // console.log('filter', filter);

  const sortOptions = useMemo(() => {
    return channelType === 'social' ? sortByOptions : gameStoreSelect;
  }, [channelType]);

  const [selectRenderKeyword, setSelectRenderKeyword] = useState<{
    origin: string[];
    zh: string[];
    en: string[];
  }>({ origin: [], zh: [], en: [] });

  const [selectKeyword, setSelectKeyword] = useState<Array<string>>([]);

  const handleStatsDetailOnClick = (props: any) => {
    const { params, dataType, chartData, statsDetailType } = props;
    setFilter(
      getNewStatsDetailFilter({
        filter,
        dataType,
        params,
        chartData,
        statsDetailType,
      }),
    );
  };

  const [statsData, setStatsData] = useState<any>({});

  const handleStatsData = (data: any) => {
    setStatsData(data);

    if (typeof data['social_total'] === 'undefined' || !data['social_total']) {
      // 先简单处理，第一个tab就是 social_media，为空 直接跳第二个去
      setChannelType('comments');
    } else {
      setChannelType('social');
    }
  };

  const addTotalString = (key: string) => {
    if (typeof statsData[key] !== 'undefined') {
      return ` (${statsData[key]})`;
    }
    return '';
  };

  const filterParams = useMemo(() => {
    let { version_times, version } = filter || {};

    if (version?.length > 0) {
      return {
        ...filter,
        version_times: version_times.filter((item: any) =>
          version.includes(item.release_time),
        ),
      };
    }

    return filter;
  }, [filter]);

  const { Sentiment_Rating } = useGetSingePageSourceRule({
    keys: 'Sentiment_Rating',
  });

  // 缓存失效：展示统一兜底提示（含「返回首页」按钮跳 /opinion）
  if (invalid) {
    return (
      <Content className="dbo-keywords-details">
        <ContentBody>
          <CacheExpiredTips scene="drilldown" />
        </ContentBody>
      </Content>
    );
  }

  return (
    <Content className="dbo-keywords-details">
      <ContentBody
        loading={loading}
        contentHeard={
          <Content.Header
            showBackButton={title !== 'Keyword Related Reviews'}
            onBackButtonClick={() => history.goBack()}
            title={formatMessage({
              id: title || NO_DATE.EMPTY,
              defaultMessage: title || NO_DATE.EMPTY,
            })}
            className={hasHeaderGame ? 'keywords-details-header' : ''}
            // 隐藏背景图片
            // style={{
            //   backgroundImage: `url(${headerGameInfo?.entity_image})`,
            // }}
          >
            {/* 因为此详情页面背景图片要加透明度， 所以不能放上面，会影响里面样式内容 */}
            <div className="keywords-details-header-bg">
              <img
                className="keywords-details-header-img"
                src={headerGameInfo?.entity_image || videoCover}
              />
            </div>
            {hasHeaderGame && (
              <HeaderGame gameInfo={headerGameInfo}></HeaderGame>
            )}
          </Content.Header>
        }
      >
        {/* 这里插入各种图表 */}
        {/* keyword下钻没有图表 */}
        {statsDetailChartsData?.dataType && (
          <Charts
            defaultSelected={defaultSelected}
            statsDetailType={statsDetailType}
            statsDetailChartsData={statsDetailChartsData}
            filter={filter}
            handleStatsDetailOnClick={handleStatsDetailOnClick}
            rankBy={rankBy}
          ></Charts>
        )}

        {statsDetailType === 'steam-ratings-language' && (
          <LanguageSubtitle
            filter={filter}
            statsDetailChartsData={statsDetailChartsData}
          />
        )}

        <StatsDetailKeyword
          userInfo={userInfo}
          gameInfo={gameInfo}
          setSelectRenderKeyword={setSelectRenderKeyword}
          filter={filterParams}
          tableTotal={total}
          needBrandHealth={needBrandHealth}
          backCommentTotal={(value: number) => setTotal(value || 0)}
          statsDetailType={statsDetailType}
          selectKeyword={selectKeyword}
          setSelectKeyword={(value) => {
            setSelectKeyword(value);
          }}
          statsDetailChartsData={statsDetailChartsData}
          handleStatsData={handleStatsData}
          commentLoading={feedsCommentsLoading}
        ></StatsDetailKeyword>

        <FilterBoxCommon
          className="dbo-stats-selectbox"
          left={[
            {
              key: 'sortBy',
              defaultValue: sortBy.by,
              defaultParams: {
                options: sortOptions,
              },
            },
          ]}
          right={[
            {
              key: 'download',
              event: {
                onClick: () => {
                  const map = {
                    social: 'Social Media Feeds',
                    comments: 'Game Store Feeds',
                    news: 'News Media Feed',
                  };

                  const name = map[channelType];

                  const downloadLimit = 150000; //下载限制条数

                  const gameName = wholeGameInfo?.entity_name || '';

                  const fileName =
                    gameName + ' ' + name + ' - DataBrain Opinions';

                  const downLoadParams = {
                    ...gameInfo,
                    ...filter,
                    user_id: userInfo.userid,
                    review_type: 'all',
                    translated_to: 'origin',
                    statistics_field:
                      statsDetailChartsData?.dataType || 'keyword',
                    channel_type: channelType,
                    sort_item: sortBy.by,
                    sort_model: sortBy.order,
                    keyword: selectKeyword,
                    hideSentimentRating: Sentiment_Rating,
                    page: 1,
                    page_size: 1000,
                  };

                  downloadLegacy({
                    limit: downloadLimit,
                    fileName,
                    params: downLoadParams,
                    downloadFunc: 'getReplayDownload',
                    sheetName: name,
                    total: totalMap?.[channelType] || 0,
                  });
                },
              },
            },
          ]}
          onChange={(value, key) => {
            if (key?.includes('sortBy')) {
              setSortBy({
                order: 'desc',
                by: value[key],
              });
            }
          }}
        ></FilterBoxCommon>

        <CardCommon className="details-table">
          <>
            <Justify
              className="dbo-stats-feeds-justify"
              left={
                <Segment
                  value={channelType.toString()}
                  onChange={(value: ChannelType) => {
                    setChannelType(value);
                    if (value === 'social') {
                      setSortBy({ order: 'desc', by: 'engagement' });
                    } else {
                      setSortBy({ order: 'desc', by: 'comment_time' });
                    }
                  }}
                  options={[
                    {
                      text:
                        formatMessage({ id: 'Social Media' }) +
                        addTotalString('social_total'),
                      value: 'social',
                    },
                    {
                      text:
                        formatMessage({ id: 'Game Store' }) +
                        addTotalString('comments_total'),
                      value: 'comments',
                    },
                  ]}
                />
              }
              right={
                <>
                  <Button
                    type="link"
                    className="details-more"
                    onClick={() => {
                      handleViewMore(channelType, history);
                    }}
                  >
                    {formatMessage({ id: 'View More' })}

                    <span>
                      <SvgIcon
                        type="arrow-right"
                        className="details-more-icon"
                      />
                    </span>
                  </Button>
                </>
              }
            />

            {channelType === 'social' && (
              <StatsTable
                setSortBy={setSortBy}
                sortBy={sortBy}
                renderKeyword={selectRenderKeyword}
                userInfo={userInfo}
                gameInfo={gameInfo}
                channelType={'social'}
                filter={filterParams}
                statsDetailType={statsDetailType}
                selectKeyword={selectKeyword}
                statsDetailChartsData={statsDetailChartsData}
                backLoading={setFeedsCommentsLoading}
                backCommentTotal={(value) => {
                  setTotalMap({ ...totalMap, ['social']: value });
                }}
              />
            )}

            {channelType === 'comments' && (
              <StatsTable
                setSortBy={setSortBy}
                sortBy={sortBy}
                userInfo={userInfo}
                gameInfo={gameInfo}
                channelType={'comments'}
                renderKeyword={selectRenderKeyword}
                filter={filterParams}
                statsDetailType={statsDetailType}
                selectKeyword={selectKeyword}
                statsDetailChartsData={statsDetailChartsData}
                backLoading={setFeedsCommentsLoading}
                backCommentTotal={(value) => {
                  setTotalMap({ ...totalMap, ['comments']: value });
                }}
              />
            )}
          </>
        </CardCommon>
      </ContentBody>
    </Content>
  );
}

export default connect((state: any) => {
  const { loading, home, opinionCommon } = state;

  return {
    userInfoLoading: loading.effects['home/getUserInfo'],
    userInfo: home.userInfo,
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
  };
})(StatsDetail);

/**
 * TODO: 其他系统使用
 * 详情页面跳转功能
 * @param param0
 */
export function goStatsDetailAction({
  params,
  action,
  path = '/opinion/Stats/Detail',
  extra = '',
  stats = {},
  jumpType,
  windowParams,
}: {
  params: any;
  action?: () => void;
  path?: string;
  extra?: string;
  stats?: { [key: string]: any };
  jumpType?: JumpType;
  windowParams?: any;
}) {
  const downloadId = uuidv4();

  // 写入失败时safeSetItem内部会逐级清理并提示，此时无需继续跳转
  if (!setStatsDetailParams(params, downloadId)) return;

  const splitString = path?.includes('?') ? '&' : '?';

  const resolvedUrl = `${path}${splitString}${statsDetailIdKey}=${downloadId}${extra}`;

  navigate(resolvedUrl, {
    jumpType,
    windowParams,
    historyState: stats,
  });
  action?.();
}
